import React, { useState } from 'react';
import styled from '@emotion/styled';
import PricingForm from '../components/PricingForm';
import PageHeader from '../components/PageHeader';
import { redcloudAddOns, redcloudPlans } from '../data/redcloudPlans';
import { useNavigate } from 'react-router-dom';

const PricingContainer = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;
  overflow-x: auto;
  padding-bottom: 1rem;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, minmax(280px, 1fr));
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const PlanCard = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  padding: 1.75rem;
  border-radius: 15px;
  text-align: center;
  transition: transform 0.2s;
  min-width: 260px;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
  }

  h2 {
    color: #ff3333;
    margin-bottom: 0.5rem;
    font-size: 1.5rem;
  }

  .price {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
    
    span {
      font-size: 1rem;
      opacity: 0.8;
    }
  }

  .storage {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
    opacity: 0.9;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 1.5rem 0;
    text-align: left;
    flex-grow: 1;
  }

  li {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    position: relative;
    font-size: 0.9rem;
    line-height: 1.4;

    &::before {
      content: "✓";
      position: absolute;
      left: 0;
      color: #ff3333;
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    
    h2 {
      font-size: 1.25rem;
    }
    
    .price {
      font-size: 1.5rem;
      
      span {
        font-size: 0.9rem;
      }
    }
    
    .storage {
      font-size: 1rem;
    }
    
    li {
      font-size: 0.85rem;
    }
  }
`;

const Button = styled.button`
  background: #ff3333;
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #ff4444;
  }
`;

const Description = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 15px;
  text-align: center;
  margin-bottom: 2rem;
  
  p {
    font-size: 1.2rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    max-width: 800px;
    margin: 0 auto;
    
    .red {
      color: #ff3333;
    }
  }
`;

const PromoLink = styled.a`
  color: #ff3333;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const AddOnSection = styled.div`
  margin-top: 3rem;
  margin-bottom: 2rem;
`;

const AddOnCard = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 15px;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const AddOnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const AddOnTitle = styled.h3`
  color: #ff3333;
  font-size: 1.8rem;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const AddOnPrice = styled.div`
  font-size: 1.5rem;
  
  span {
    font-size: 1rem;
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const AddOnFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;

  .red {
    color: #ff3333;
  }
  
  li {
    margin: 0.8rem 0;
    padding-left: 1.5rem;
    position: relative;
    font-size: 1rem;
    line-height: 1.5;
    
    &::before {
      content: "✓";
      position: absolute;
      left: 0;
      color: #ff3333;
    }
  }
`;

function Pricing() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const clientOfClientAddOn = redcloudAddOns.find(
    (a) => a.key === 'client-of-client-management'
  );

  const plans = redcloudPlans.map((p) => ({
    name: p.name,
    price: `$${p.monthlyPriceUsd.toFixed(2)}`,
    storage: `${p.storageTb}TB`,
    features: [
      `${p.storageTb}TB Storage`,
      `Included monthly transfer: ${p.includedMonthlyTransfer}`,
      'Advanced Sharing',
      'Mobile Access',
      '24/7 Support',
      'Version Control'
    ]
  }));

  const handleGetStarted = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  return (
    <PricingContainer>
      <PageHeader title="Simple Transparent Pricing" highlightWord="Transparent" />
      <Description>
        <p>
          Try our storage with a free <span className="red"><b>50GB</b></span> for 30 days! Visit our{' '}
          <PromoLink href="https://my.redcloudstorage.net" target="_blank" rel="noopener noreferrer">
            Client Portal
          </PromoLink>{' '}
          and register to start your free trial today. Experience our premium features and seamless cloud storage before choosing your plan.
        </p>
      </Description>
      <PlansContainer>
        {plans.map((plan, index) => (
          <PlanCard key={index}>
            <h2>{plan.name}</h2>
            <div className="price">{plan.price}<span>/month</span></div>
            <div className="storage">{plan.storage}</div>
            <ul>
              {plan.features.map((feature, idx) => (
                <li key={idx}>{feature}</li>
              ))}
            </ul>
            <Button onClick={() => handleGetStarted(plan)}>Get Started</Button>
          </PlanCard>
        ))}
      </PlansContainer>
      
      <AddOnSection>
        <PageHeader title="Premium Add-ons" highlightWord="Add-ons" />
        <AddOnCard>
          <AddOnHeader>
            <AddOnTitle>{clientOfClientAddOn?.name ?? 'Customer Storage & Gallery'}</AddOnTitle>
            <AddOnPrice>
              ${clientOfClientAddOn?.monthlyPriceUsd?.toFixed(2) ?? '2.99'}
              <span>/month</span>
            </AddOnPrice>
          </AddOnHeader>
          <AddOnFeatures>
            <li>We onboard and manage your clients for you</li>
            <li>
              We contact them, create their Red<span className="red">Cloud</span> account, and transfer the folder that contains their delivered media
            </li>
            <li>${clientOfClientAddOn?.monthlyPriceUsd?.toFixed(2) ?? '2.99'}/month (paid by your customer to us directly) for up to {clientOfClientAddOn?.includedStorageGb ?? 300}GB storage</li>
          </AddOnFeatures>
          <Button 
            style={{ marginTop: '1.5rem' }} 
            onClick={() =>
              navigate('/ownership-transfer')
            }
          >
            Request Transfer
          </Button>
        </AddOnCard>
      </AddOnSection>
      
      {selectedPlan && (
        <PricingForm
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          planName={selectedPlan.name}
          planPrice={selectedPlan.price}
        />
      )}
    </PricingContainer>
  );
}

export default Pricing;
